import axios from 'axios';
import { getDeviceInfo, storeDeviceFingerprint, getStoredDeviceFingerprint, isSameDevice } from '../utils/deviceFingerprint';
import { getApiBaseUrl } from '../config/api';

// Create axios instance with base configuration
// Use relative URLs in production (when REACT_APP_API_BASE is empty or undefined)
// This allows nginx to proxy /api/ requests to the backend
const api = axios.create({
  baseURL: getApiBaseUrl(),
});

// Request interceptor to add token and initial cache-busting
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add timestamp only for initial fresh content (no cache-busting headers)
    if (config.method === 'get') {
      config.params = {
        ...config.params,
        _t: Date.now() // Add timestamp parameter for fresh initial load
      };
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Check if the error is due to token expiration (401 or 403)
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      const url = error.config?.url || '';
      
      // Don't redirect on setup/non-critical API calls
      const isSetupCall = url.includes('/add-b2c-column') || 
                         url.includes('/check-auto-login') ||
                         url.includes('/api/me') ||
                         url.includes('/api/users') ||
                         url.includes('/api/reports/summary') ||
                         url.includes('/api/fda-selected-buttons');
      
      if (isSetupCall) {
        console.log('⚠️ Setup call failed (401/403), but not redirecting:', url);
        return Promise.reject(error);
      }
      
      // Only redirect on actual authentication failures (not setup calls)
      // Check if this is a real auth failure by checking error message
      const errorMessage = error.response?.data?.message || '';
      const errorCode = error.response?.data?.code || '';
      // The server's expired-token answer is "Token expired. Please login again."
      // with code TOKEN_EXPIRED — it matched none of the phrases below, so an
      // expired session was being logged as "not an auth failure" and the
      // user left on a dead page. Match the codes as well as the messages.
      const isAuthFailure = errorCode === 'TOKEN_EXPIRED' ||
                           errorCode === 'JWT_INVALID' ||
                           errorMessage.includes('Access denied') ||
                           errorMessage.includes('Invalid token') ||
                           errorMessage.includes('Token expired') ||
                           errorMessage.includes('Session expired');
      
      if (isAuthFailure) {
        console.log('🔒 Authentication failed, redirecting to login...');
        
        // Clear the token and username from localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('lastUsername');
        
        // Tell the app. <AuthWatcher/> in App.js listens for this and navigates
        // to "/" client-side, so the hand-back is one smooth route change
        // rather than a full page reload. The hard redirect below is only a
        // fallback for the case where nothing is listening.
        window.dispatchEvent(new CustomEvent('biomed:auth-expired', { detail: { reason: 'expired' } }));
        setTimeout(() => {
          if (window.location.pathname !== '/' && !localStorage.getItem('token')) {
            window.location.href = '/';
          }
        }, 2000);
      } else {
        console.log('⚠️ 401/403 error but not an auth failure, not redirecting:', errorMessage);
      }
    }
    return Promise.reject(error);
  }
);

// Authentication service methods
export const authService = {
  // Login method with device fingerprinting
  login: async (credentials) => {
    try {
      // Get or create device fingerprint
      let deviceData = getStoredDeviceFingerprint();
      if (!deviceData) {
        deviceData = storeDeviceFingerprint();
      }

      const loginData = {
        ...credentials,
        deviceId: deviceData.deviceId,
        deviceFingerprintHash: deviceData.hash
      };

      const response = await api.post('/api/login', loginData);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        
        // Store username for potential auto-login (always store for regular login)
        localStorage.setItem('lastUsername', credentials.username);
        
        // Start periodic token refresh
        authService.startTokenRefresh();
      }
      return response.data;
    } catch (error) {
      if (error.response?.status === 409) {
        // Session exists error
        throw new Error('User is already logged in on another device. Please logout from the other device first or wait for the session to expire (3 hours).');
      }
      throw error;
    }
  },

  // Check if user can auto-login
  checkAutoLogin: async (username) => {
    try {
      const deviceData = getStoredDeviceFingerprint();
      if (!deviceData) {
        return null;
      }

      const response = await api.post('/api/check-auto-login', {
        username,
        deviceFingerprintHash: deviceData.hash
      });

      if (response.data.canAutoLogin && response.data.token) {
        localStorage.setItem('token', response.data.token);
        return response.data;
      }

      return null;
    } catch (error) {
      console.error('Auto-login check failed:', error);
      return null;
    }
  },

  // Attempt auto-login on app start
  attemptAutoLogin: async () => {
    try {
      const lastUsername = localStorage.getItem('lastUsername');
      const deviceData = getStoredDeviceFingerprint();
      
      console.log('🔍 Auto-login attempt:', { lastUsername, hasDeviceData: !!deviceData });
      
      if (!lastUsername || !deviceData) {
        console.log('❌ Auto-login failed: Missing username or device data');
        return null;
      }

      // Check if we're on the same device
      const sameDevice = isSameDevice();
      console.log('🔍 Same device check:', sameDevice);
      
      if (!sameDevice) {
        console.log('❌ Auto-login failed: Different device detected');
        localStorage.removeItem('lastUsername');
        return null;
      }

      console.log('🔍 Attempting auto-login for:', lastUsername);
      const autoLoginResult = await authService.checkAutoLogin(lastUsername);
      console.log('🔍 Auto-login result:', autoLoginResult ? 'Success' : 'Failed');
      return autoLoginResult;
    } catch (error) {
      console.error('Auto-login attempt failed:', error);
      return null;
    }
  },

  // Logout method
  logout: async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await api.post('/api/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Stop token refresh
      authService.stopTokenRefresh();
      
      localStorage.removeItem('token');
      localStorage.removeItem('lastUsername');
      window.location.href = '/';
    }
  },

  // Check if user is authenticated
  // A token that has passed its exp claim is not authentication. The JWT and
  // the server session both live 3h, so exp is a reliable local signal — and
  // it is what lets the app send the user back to the login page on its own
  // instead of waiting for the next request to fail.
  isAuthenticated: () => {
    const token = localStorage.getItem('token');
    return !!token && !authService.isTokenExpired(token);
  },

  // Get current token
  getToken: () => {
    return localStorage.getItem('token');
  },

  // Check if token is expired
  isTokenExpired: (token) => {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      return true;
    }
  },

  // Refresh token if needed
  refreshTokenIfNeeded: async () => {
    const token = localStorage.getItem('token');
    if (!token || authService.isTokenExpired(token)) {
      console.log('🔄 Token expired, attempting auto-login...');
      const lastUsername = localStorage.getItem('lastUsername');
      if (lastUsername) {
        try {
          const autoLoginResult = await authService.checkAutoLogin(lastUsername);
          if (autoLoginResult && autoLoginResult.token) {
            localStorage.setItem('token', autoLoginResult.token);
            return autoLoginResult.token;
          }
        } catch (error) {
          console.log('Auto-login failed:', error);
        }
      }
      return null;
    }
    return token;
  },

  // Verify token validity
  verifyToken: async () => {
    try {
      const response = await api.get('/api/me');
      return response.data;
    } catch (error) {
      // Don't automatically logout - let the caller handle it
      // This prevents redirect loops when API is temporarily unavailable
      console.error('Token verification failed:', error);
      throw error;
    }
  },

  // Get device information
  getDeviceInfo: () => {
    return getDeviceInfo();
  },

  // Check if current device matches stored fingerprint
  isSameDevice: () => {
    return isSameDevice();
  },

  // Start periodic token refresh (call this when user logs in)
  startTokenRefresh: () => {
    // Check token every 30 minutes
    const refreshInterval = setInterval(async () => {
      const token = localStorage.getItem('token');
      if (token && authService.isTokenExpired(token)) {
        console.log('🔄 Token refresh needed...');
        const lastUsername = localStorage.getItem('lastUsername');
        if (lastUsername) {
          try {
            const autoLoginResult = await authService.checkAutoLogin(lastUsername);
            if (autoLoginResult && autoLoginResult.token) {
              localStorage.setItem('token', autoLoginResult.token);
              console.log('✅ Token refreshed successfully');
            } else {
              console.log('❌ Token refresh failed, clearing session');
              localStorage.removeItem('token');
              localStorage.removeItem('lastUsername');
              clearInterval(refreshInterval);
            }
          } catch (error) {
            console.log('❌ Token refresh error:', error);
            localStorage.removeItem('token');
            localStorage.removeItem('lastUsername');
            clearInterval(refreshInterval);
          }
        }
      }
    }, 30 * 60 * 1000); // 30 minutes

    // Store interval ID for cleanup
    localStorage.setItem('tokenRefreshInterval', refreshInterval);
  },

  // Stop periodic token refresh (call this when user logs out)
  stopTokenRefresh: () => {
    const intervalId = localStorage.getItem('tokenRefreshInterval');
    if (intervalId) {
      clearInterval(parseInt(intervalId));
      localStorage.removeItem('tokenRefreshInterval');
    }
  }
};

export default api; 